import React, { useEffect, useRef, useState } from 'react'

import { Heading, Text } from '@hhgtech/hhg-components/components'
import Link from 'next/link'
import { useIntl } from 'react-intl'

import { useRouterExtend } from 'hooks/useRouterExtend'
import { DEFAULT_RESULT_IMG } from 'interfaces/constants'
import { ReviewedCategory } from 'interfaces/types'
import { renderDate } from 'utils/renderDate'

import {
  StyledCategoryInfoMobile,
  StyledCategoryInfoDesktop,
} from './index.styled'

export type Props = {
  imgBannerSrc: string
  categoryName: string
  description: string
  isMobile: boolean
  colorFeatureImage: string
  categoryTextColor: string
  reviewedBy?: ReviewedCategory
  reviewedDate?: string
}

type PropsChildren = {
  imgBannerSrc: string
  categoryName: string
  description: string
  colorFeatureImage: string
  reviewedBy?: ReviewedCategory
  reviewedDate?: string
}

const RenderDesktop = ({
  imgBannerSrc,
  categoryName,
  description,
  colorFeatureImage,
  reviewedBy,
  reviewedDate,
}: PropsChildren) => {
  const { formatMessage: f } = useIntl()
  const { locale } = useRouterExtend()
  return (
    <StyledCategoryInfoDesktop categoryColor={colorFeatureImage}>
      <div className="heading">
        <div className="banner">
          <img
            loading="lazy"
            src={imgBannerSrc || DEFAULT_RESULT_IMG}
            style={{ width: '140px', height: '140px', objectFit: 'contain' }}
            alt={categoryName}
          />
        </div>

        <div className="info">
          <Heading className="category-name" tag="h3" as="h1">
            {categoryName}
          </Heading>

          {reviewedBy && (
            <div className="reviewed">
              <Text size="p3">
                {f({
                  id: 'categoryPage.info.reviewed',
                })}{' '}
                <Link href={reviewedBy.profileUrl} prefetch={false}>
                  {reviewedBy.displayName}
                </Link>
                {' ' +
                  f(
                    { id: 'categoryPage.onDate', defaultMessage: 'on {date}' },
                    { date: renderDate(reviewedDate, locale) },
                  )}
              </Text>
            </div>
          )}
        </div>
      </div>

      {description ? (
        <div className="content">
          <div className="description">
            <Text size="p3">{description}</Text>
          </div>
        </div>
      ) : (
        <div className="content no-description"></div>
      )}
    </StyledCategoryInfoDesktop>
  )
}

const RenderMobile = ({
  imgBannerSrc,
  categoryName,
  description,
  colorFeatureImage,
  reviewedBy,
  reviewedDate,
}: PropsChildren) => {
  const refInfo = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState<number | undefined>(0)
  const { formatMessage: f } = useIntl()
  const { locale } = useRouterExtend()
  useEffect(() => {
    setHeight(refInfo.current?.clientHeight)
  }, [])

  return (
    <StyledCategoryInfoMobile categoryColor={colorFeatureImage}>
      <div className="heading">
        <div className="banner">
          <img
            loading="lazy"
            src={imgBannerSrc || DEFAULT_RESULT_IMG}
            style={{
              width: '80px',
              height: '80px',
              objectFit: 'contain',
            }}
            alt={categoryName}
          />
        </div>

        <div ref={refInfo} className="info">
          <Heading className="category-name" tag="h3" as="h1">
            {categoryName}
          </Heading>

          {reviewedBy && (
            <div className="reviewed">
              <Text size="p3">
                {f({
                  id: 'categoryPage.info.reviewed',
                })}{' '}
                <Link href={reviewedBy.profileUrl} prefetch={false}>
                  {reviewedBy.displayName}
                </Link>
                {' ' +
                  f(
                    { id: 'categoryPage.onDate', defaultMessage: 'on {date}' },
                    { date: renderDate(reviewedDate, locale) },
                  )}
              </Text>
            </div>
          )}
        </div>
      </div>

      <div
        className="content"
        style={{ marginTop: height ? `-${height + 50}px` : '-84px' }}
      >
        {description ? (
          <div
            className="description"
            style={{ marginTop: height ? `${height + 50}px` : '84px' }}
          >
            <Text size="p3">{description}</Text>
          </div>
        ) : (
          <div
            className="description no-des"
            style={{ marginTop: height ? `${height + 50}px` : '84px' }}
          ></div>
        )}
      </div>
    </StyledCategoryInfoMobile>
  )
}

const CategoryInfo = ({
  imgBannerSrc,
  categoryName,
  description,
  isMobile,
  colorFeatureImage,
  reviewedBy,
  reviewedDate,
}: Props) => {
  return !isMobile ? (
    <RenderDesktop
      imgBannerSrc={imgBannerSrc}
      categoryName={categoryName}
      description={description}
      colorFeatureImage={colorFeatureImage}
      reviewedBy={reviewedBy}
      reviewedDate={reviewedDate}
    />
  ) : (
    <RenderMobile
      imgBannerSrc={imgBannerSrc}
      categoryName={categoryName}
      description={description}
      colorFeatureImage={colorFeatureImage}
      reviewedBy={reviewedBy}
      reviewedDate={reviewedDate}
    />
  )
}

export { CategoryInfo }
